// Labels, badge classes and legend copy for these two scales live in ./risk, which
// carries no state prose — so route head() functions and the map legend can import
// the vocabulary without pulling this 60KB module along.
import type { RiskLevel, ConfidenceLevel } from "./risk";

export type { RiskLevel, ConfidenceLevel };

/**
 * What a county rating rests on: a source id from ./sources and, in a sentence, what that
 * source says about this county. Required, so a rating cannot be added without one.
 */
export interface CountySource {
  sourceId: string;
  finding: string;
}

/**
 * A named county on a state page. "high" (shown as "Alpha-gal hotspot") is used only
 * where CDC names the county for its alpha-gal case count or rate; every other county
 * is "established" ("Found here") on the strength of CDC's county tick records.
 */
export interface CountySummary {
  name: string;
  awarenessLevel: RiskLevel;
  source: CountySource;
}

/** CDC's 2025 county record says Lone Star ticks are established here. */
const CDC_ESTABLISHED: CountySource = {
  sourceId: "cdc-lst-county-data",
  finding: "CDC county record, 2025: established.",
};

/** CDC has a record, but below its "established" threshold. */
const CDC_REPORTED: CountySource = {
  sourceId: "cdc-lst-county-data",
  finding:
    "CDC county record, 2025: reported, though not in the numbers CDC needs to call it established.",
};

/** The four counties CDC's 2023 MMWR names, quoted with its figures. */
const MMWR_NAMED = {
  suffolk: {
    sourceId: "cdc-ags-distribution",
    finding:
      "3,746 suspected alpha-gal cases in 2017–2022, the most of any county CDC named, and about 4% of the U.S. total.",
  },
  bedford: {
    sourceId: "cdc-ags-distribution",
    finding:
      "1,511 suspected alpha-gal cases in 2017–2022. CDC names it with Suffolk County, N.Y., as having the most cases.",
  },
  charlotte: {
    sourceId: "cdc-ags-distribution",
    finding:
      "12,273 suspected alpha-gal cases per million people a year in 2017–2022, the highest rate CDC named. CDC has no tick record for the county itself.",
  },
  muhlenberg: {
    sourceId: "cdc-ags-distribution",
    finding:
      "6,107 suspected alpha-gal cases per million people a year in 2017–2022, one of the two highest rates CDC named.",
  },
} satisfies Record<string, CountySource>;

export interface SeasonSource {
  sourceId: string;
  scope: "state" | "regional" | "national";
}

export interface StateInfo {
  slug: string;
  name: string;
  abbr: string;
  risk: RiskLevel;
  peakSeason: string;
  /**
   * Where the peak-season months come from. "state" means a source about this state;
   * "regional" a source about a neighbour or the wider region; "national" CDC's general
   * guidance. The page says which, so a borrowed figure never passes for a local one.
   */
  seasonSource: SeasonSource;
  summary: string;
  awareness: string;
  // --- extended model (see plan: county/ZIP-ready data architecture) ---
  habitats: string[];
  preventionNotes: string[];
  whoShouldBeAware: string[];
  sourceIds: string[];
  lastReviewed: string;
  disclaimerText?: string;
  nearbyStateSlugs?: string[];
  countyData?: CountySummary[];
}

const DEFAULT_HABITATS = [
  "Wooded areas and shaded trail edges",
  "Tall grass, brush, and overgrown fields",
  "Leaf litter and ground cover",
  "Shaded borders where lawns meet woods",
];

const DEFAULT_PREVENTION = [
  "Use EPA-registered repellent (DEET, picaridin, or oil of lemon eucalyptus)",
  "Treat clothing and gear with permethrin",
  "Wear long sleeves and tuck pants into socks in brushy areas",
  "Stay on cleared trails and avoid tall grass and leaf litter",
  "Shower within two hours of coming indoors",
  "Do daily tick checks on people and pets during the season",
];

const DEFAULT_AUDIENCE = [
  "Hikers & campers",
  "Hunters",
  "Pet owners",
  "Gardeners & yard workers",
  "Golfers",
  "Parents of young children",
];

// Source sets by tier. Every state cites real, specific sources (see src/lib/sources.ts).
// High/established states sit within the CDC-documented AGS region, so they cite the
// AGS geographic-distribution report; low/limited states cite the surveillance data that
// shows the Lone Star tick is rare or not established there.
const SRC_AGS_REGION = [
  "cdc-tick-geography",
  "cdc-lone-star-surveillance",
  "cdc-ags",
  "cdc-ags-distribution",
  "plos-aa-distribution",
  "cdc-prevent-bites",
  "epa-repellent-search",
];
const SRC_EMERGING = [
  "cdc-tick-geography",
  "cdc-lone-star-surveillance",
  "cdc-ags",
  "plos-aa-distribution",
  "cdc-prevent-bites",
  "epa-repellent-search",
];
const SRC_LIMITED = [
  "cdc-tick-geography",
  "cdc-lone-star-surveillance",
  "cdc-ags",
  "cdc-prevent-bites",
  "epa-repellent-search",
  "state-health-departments",
];

const DEFAULT_SOURCE_IDS = SRC_LIMITED;

type StateSeed = Pick<
  StateInfo,
  "slug" | "name" | "abbr" | "risk" | "peakSeason" | "seasonSource" | "summary" | "awareness"
> &
  Partial<StateInfo>;

// Classifications reviewed against CDC "Where Ticks Live" + Lone Star Tick Surveillance,
// the CDC MMWR (2023) AGS geographic-distribution report, and PLOS ONE niche modeling.
// "high" = established tick range AND among the highest reported AGS prevalence per CDC.
// Where data is sparse (much of the West/Mountain/Pacific), states are kept at low/limited
// and say so — presence is never inflated. County rows are only added where sourced.
const SEED: StateSeed[] = [
  {
    slug: "alabama",
    name: "Alabama",
    abbr: "AL",
    risk: "established",
    peakSeason: "March – October",
    seasonSource: { sourceId: "adph-tick", scope: "state" },
    summary:
      "Lone Star ticks are established across much of Alabama within the southeastern range.",
    awareness:
      "Alabama sits in the established Lone Star tick range. Outdoor workers, hunters, and rural residents should take prevention steps during the warm season.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "adph-tick"],
    nearbyStateSlugs: ["georgia", "mississippi", "tennessee", "florida"],
  },
  {
    slug: "alaska",
    name: "Alaska",
    abbr: "AK",
    risk: "low",
    peakSeason: "Not found here",
    seasonSource: { sourceId: "cdc-tick-geography", scope: "state" },
    summary: "Lone Star ticks are not established in Alaska based on available surveillance data.",
    awareness:
      "Reported Lone Star tick presence in Alaska is limited or absent. Other tick species may occur; general tick precautions still apply outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "arizona",
    name: "Arizona",
    abbr: "AZ",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Arizona based on public surveillance.",
    awareness:
      "Arizona lies outside the documented Lone Star tick range. Other tick species are present, so general prevention is still wise.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
    nearbyStateSlugs: ["new-mexico", "nevada", "california", "utah"],
  },
  {
    slug: "arkansas",
    name: "Arkansas",
    abbr: "AR",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "Arkansas is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC lists Lone Star ticks as established in 73 of Arkansas's 75 counties. Hunters, outdoor workers and rural residents should take precautions.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data"],
    nearbyStateSlugs: ["missouri", "tennessee", "mississippi", "louisiana", "oklahoma", "texas"],
    countyData: [
      {
        name: "Pulaski County (Little Rock area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Washington County (Fayetteville area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Benton County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      {
        name: "Garland County (Hot Springs area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
    ],
  },
  {
    slug: "california",
    name: "California",
    abbr: "CA",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star ticks are rare or not established in California based on available surveillance data.",
    awareness:
      "California is west of the documented Lone Star tick range. Other tick species are common in the state, so follow general tick prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
    nearbyStateSlugs: ["oregon", "nevada", "arizona"],
  },
  {
    slug: "colorado",
    name: "Colorado",
    abbr: "CO",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star ticks are rare or not established in Colorado based on public surveillance.",
    awareness:
      "A few Lone Star ticks have been reported in Colorado, but CDC lists no county where they are established. Other ticks live here, so general tick safety still matters.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
    nearbyStateSlugs: ["kansas", "nebraska", "new-mexico", "wyoming", "utah"],
  },
  {
    slug: "connecticut",
    name: "Connecticut",
    abbr: "CT",
    risk: "emerging",
    peakSeason: "April – August",
    seasonSource: { sourceId: "uri-tickencounter-lst", scope: "regional" },
    summary:
      "Lone Star ticks have been found in Connecticut, and a large population turned up on the coast at Norwalk in 2017.",
    awareness:
      "CDC now lists three Connecticut counties where the tick is established: Fairfield, New Haven and New London. Follow tick prevention in brushy and wooded spots.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "caes-2017-lst", "uri-tickencounter-lst"],
    nearbyStateSlugs: ["new-york", "massachusetts", "rhode-island"],
  },
  {
    slug: "delaware",
    name: "Delaware",
    abbr: "DE",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "Delaware is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "Residents and outdoor workers should take prevention seriously during the warm months.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data"],
    nearbyStateSlugs: ["maryland", "new-jersey", "pennsylvania"],
  },
  {
    slug: "district-of-columbia",
    name: "District of Columbia",
    abbr: "DC",
    risk: "established",
    peakSeason: "April – September",
    seasonSource: { sourceId: "umd-ext-ticks", scope: "regional" },
    summary:
      "Washington, D.C. sits within the mid-Atlantic Lone Star tick range, between Maryland and Virginia, two states where CDC found high rates of suspected alpha-gal cases.",
    awareness:
      "The D.C. area is part of the established mid-Atlantic range. Use prevention in parks, trail edges, and wooded greenspaces.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "umd-ext-ticks"],
    nearbyStateSlugs: ["maryland", "virginia"],
  },
  {
    slug: "florida",
    name: "Florida",
    abbr: "FL",
    risk: "established",
    peakSeason: "Year-round (peak April – August)",
    seasonSource: { sourceId: "uf-news-2026-ticks", scope: "state" },
    summary:
      "Lone Star ticks are established across much of Florida, with longer active seasons due to climate.",
    awareness:
      "Lone Star ticks are Florida's most common human-biting tick, the University of Florida says, and the warm climate keeps them active all year.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "uf-news-2026-ticks"],
    nearbyStateSlugs: ["georgia", "alabama", "south-carolina"],
  },
  {
    slug: "georgia",
    name: "Georgia",
    abbr: "GA",
    risk: "established",
    peakSeason: "March – October",
    seasonSource: { sourceId: "ga-dph-tbd", scope: "state" },
    summary:
      "Lone Star ticks are Georgia's most common tick, and CDC lists them as established in 60 of the state's 159 counties.",
    awareness:
      "Georgia's health department lists alpha-gal syndrome among the tick illnesses it is watching, though few cases are reported to the state. Outdoor workers, hunters and homeowners should take care during the long tick season.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "ga-dph-tbd", "uga-gray-2017"],
    nearbyStateSlugs: ["south-carolina", "tennessee", "alabama", "florida", "north-carolina"],
    countyData: [
      { name: "Fulton County (Atlanta area)", awarenessLevel: "established", source: CDC_REPORTED },
      { name: "Cobb County", awarenessLevel: "established", source: CDC_REPORTED },
      {
        name: "Chatham County (Savannah area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Bibb County (Macon area)", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "Lowndes County", awarenessLevel: "established", source: CDC_REPORTED },
    ],
  },
  {
    slug: "hawaii",
    name: "Hawaii",
    abbr: "HI",
    risk: "low",
    peakSeason: "Not found here",
    seasonSource: { sourceId: "cdc-tick-geography", scope: "state" },
    summary: "Lone Star ticks are not established in Hawaii based on available surveillance data.",
    awareness:
      "Hawaii is outside the Lone Star tick range. Tick-borne risk differs from the mainland; consult local health guidance.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "idaho",
    name: "Idaho",
    abbr: "ID",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Idaho based on public surveillance.",
    awareness:
      "Idaho lies outside the documented Lone Star tick range. Other tick species occur in the region, so general prevention still applies.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "illinois",
    name: "Illinois",
    abbr: "IL",
    risk: "high",
    peakSeason: "April – July",
    seasonSource: { sourceId: "idph-common-ticks", scope: "state" },
    summary:
      "CDC lists Lone Star ticks as established in 68 of Illinois's 102 counties. The state health department says they live mainly in the southern half of the state.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Illinois. Use tick prevention in wooded and brushy areas.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "idph-common-ticks"],
    nearbyStateSlugs: ["indiana", "missouri", "kentucky", "iowa", "wisconsin"],
  },
  {
    slug: "indiana",
    name: "Indiana",
    abbr: "IN",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary: "CDC lists Lone Star ticks as established in 49 of Indiana's 92 counties.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Indiana. Follow prevention in wooded and brushy areas.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data"],
    nearbyStateSlugs: ["illinois", "ohio", "kentucky", "michigan"],
  },
  {
    slug: "iowa",
    name: "Iowa",
    abbr: "IA",
    risk: "established",
    peakSeason: "May – June",
    seasonSource: { sourceId: "isu-lst", scope: "state" },
    summary:
      "CDC lists Lone Star ticks as established in 28 Iowa counties. Iowa State University says they have become fairly common, especially in the southern half of the state.",
    awareness:
      "Use tick prevention in brushy and wooded areas, and check yourself and your pets after time outdoors.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "isu-lst"],
    nearbyStateSlugs: ["illinois", "missouri", "nebraska", "minnesota", "wisconsin"],
  },
  {
    slug: "kansas",
    name: "Kansas",
    abbr: "KS",
    risk: "high",
    peakSeason: "March – October",
    seasonSource: { sourceId: "ks-tick-guide", scope: "state" },
    summary:
      "Lone Star ticks are common in eastern Kansas, and CDC found some of the highest rates of suspected alpha-gal cases in parts of the state.",
    awareness:
      "CDC lists Lone Star ticks as established in 32 Kansas counties. K-State says they are common in the eastern half of the state and have been found as far west as Mitchell County.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "ks-tick-guide", "ksu-mf2653"],
    nearbyStateSlugs: ["missouri", "oklahoma", "nebraska", "colorado"],
  },
  {
    slug: "kentucky",
    name: "Kentucky",
    abbr: "KY",
    risk: "high",
    peakSeason: "March – September",
    seasonSource: { sourceId: "uky-ef648", scope: "state" },
    summary:
      "Kentucky is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "Muhlenberg County had one of the two highest rates of suspected alpha-gal cases of any county CDC named. Take care in rural and wooded areas, especially from March through September.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "uky-ef648"],
    nearbyStateSlugs: [
      "tennessee",
      "virginia",
      "ohio",
      "indiana",
      "illinois",
      "missouri",
      "west-virginia",
    ],
    countyData: [
      { name: "Muhlenberg County", awarenessLevel: "high", source: MMWR_NAMED.muhlenberg },
      {
        name: "Jefferson County (Louisville area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Fayette County (Lexington area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
    ],
  },
  {
    slug: "louisiana",
    name: "Louisiana",
    abbr: "LA",
    risk: "established",
    peakSeason: "March – October",
    seasonSource: { sourceId: "msu-ext-ticks", scope: "regional" },
    summary:
      "CDC lists Lone Star ticks as established in 19 of Louisiana's 64 parishes and reported in 13 more.",
    awareness:
      "Louisiana sits in the established range with a long active season. Outdoor workers, hunters, and rural residents should follow prevention steps.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "msu-ext-ticks"],
    nearbyStateSlugs: ["texas", "arkansas", "mississippi"],
  },
  {
    slug: "maine",
    name: "Maine",
    abbr: "ME",
    risk: "emerging",
    peakSeason: "April – August",
    seasonSource: { sourceId: "uri-tickencounter-lst", scope: "regional" },
    summary:
      "Lone Star ticks have been found now and then in Maine since the 1990s, but no established populations are known.",
    awareness:
      "CDC has reports from 12 Maine counties. Maine is at the northern edge of the range, so talk with a clinician if you react to red meat hours after eating it.",
    sourceIds: [
      ...SRC_EMERGING,
      "cdc-lst-county-data",
      "saunders-2025-eid-maine",
      "uri-tickencounter-lst",
    ],
    nearbyStateSlugs: ["new-hampshire", "massachusetts"],
  },
  {
    slug: "maryland",
    name: "Maryland",
    abbr: "MD",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "umd-ext-ticks", scope: "state" },
    summary:
      "Maryland is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC lists Lone Star ticks as established in 18 of Maryland's 23 counties. Residents and outdoor workers should take prevention seriously.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "umd-ext-ticks"],
    nearbyStateSlugs: [
      "virginia",
      "delaware",
      "pennsylvania",
      "west-virginia",
      "district-of-columbia",
    ],
  },
  {
    slug: "massachusetts",
    name: "Massachusetts",
    abbr: "MA",
    risk: "emerging",
    peakSeason: "April – August",
    seasonSource: { sourceId: "uri-tickencounter-lst", scope: "regional" },
    summary:
      "Lone Star ticks have been found in parts of Massachusetts. CDC lists them as established in Dukes County (Martha's Vineyard) and reported in four more counties.",
    awareness:
      "Massachusetts is near the northern edge of the range. Follow prevention in dunes, brush and woods.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "uri-tickencounter-lst"],
    nearbyStateSlugs: ["connecticut", "rhode-island", "new-hampshire", "new-york", "vermont"],
  },
  {
    slug: "michigan",
    name: "Michigan",
    abbr: "MI",
    risk: "emerging",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists Lone Star ticks as established in five Michigan counties and reported in 39 more.",
    awareness:
      "Michigan is at the northern edge of the range. Follow prevention in brushy and wooded areas.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data"],
    nearbyStateSlugs: ["indiana", "ohio", "wisconsin"],
  },
  {
    slug: "minnesota",
    name: "Minnesota",
    abbr: "MN",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "mdh-alphagal", scope: "state" },
    summary:
      "Lone Star ticks are rarely found in Minnesota, the state health department says, and CDC lists no county where they are established.",
    awareness:
      "CDC still found moderate to high rates of suspected alpha-gal cases in some Minnesota counties, so talk to a clinician about symptoms. Other ticks are common here.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "mdh-alphagal"],
    nearbyStateSlugs: ["wisconsin", "iowa", "north-dakota", "south-dakota"],
  },
  {
    slug: "mississippi",
    name: "Mississippi",
    abbr: "MS",
    risk: "high",
    peakSeason: "March – October",
    seasonSource: { sourceId: "msu-ext-ticks", scope: "state" },
    summary:
      "Mississippi is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Mississippi. Follow prevention steps through the long warm season.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "msu-ext-ticks"],
    nearbyStateSlugs: ["alabama", "louisiana", "arkansas", "tennessee"],
  },
  {
    slug: "missouri",
    name: "Missouri",
    abbr: "MO",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "mdc-avoiding-ticks", scope: "state" },
    summary:
      "Missouri has long-established Lone Star tick populations and is among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Missouri. Hunters and hikers should take precautions and check for ticks.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "mdc-avoiding-ticks"],
    nearbyStateSlugs: [
      "arkansas",
      "kansas",
      "tennessee",
      "kentucky",
      "illinois",
      "iowa",
      "oklahoma",
    ],
    countyData: [
      {
        name: "Greene County (Springfield area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Taney County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "Phelps County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "St. Louis County", awarenessLevel: "established", source: CDC_ESTABLISHED },
    ],
  },
  {
    slug: "montana",
    name: "Montana",
    abbr: "MT",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Montana based on public surveillance.",
    awareness:
      "Montana lies outside the documented Lone Star tick range. Other tick species occur, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "nebraska",
    name: "Nebraska",
    abbr: "NE",
    risk: "established",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists Lone Star ticks as established in 27 Nebraska counties and reported in 21 more.",
    awareness:
      "Follow tick prevention in brushy and wooded areas, and check yourself and your pets when you come in.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data"],
    nearbyStateSlugs: ["iowa", "kansas", "missouri", "south-dakota", "colorado"],
  },
  {
    slug: "nevada",
    name: "Nevada",
    abbr: "NV",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Nevada based on public surveillance.",
    awareness:
      "Nevada lies outside the documented Lone Star tick range. General tick prevention still applies in vegetated areas.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "new-hampshire",
    name: "New Hampshire",
    abbr: "NH",
    risk: "emerging",
    peakSeason: "April – August",
    seasonSource: { sourceId: "uri-tickencounter-lst", scope: "regional" },
    summary:
      "CDC lists one New Hampshire county where Lone Star ticks are established (Merrimack) and one more where they have been reported.",
    awareness:
      "New Hampshire is at the northern edge of the range. Follow prevention in brushy and wooded areas.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "uri-tickencounter-lst"],
    nearbyStateSlugs: ["maine", "massachusetts", "vermont"],
  },
  {
    slug: "new-jersey",
    name: "New Jersey",
    abbr: "NJ",
    risk: "established",
    peakSeason: "April – September",
    seasonSource: { sourceId: "rutgers-fs1281", scope: "state" },
    summary: "Lone Star ticks are established across much of New Jersey.",
    awareness:
      "CDC lists Lone Star ticks as established in 14 of New Jersey's 21 counties. Follow tick prevention in wooded and grassy areas.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "rutgers-fs1281"],
    nearbyStateSlugs: ["new-york", "pennsylvania", "delaware"],
  },
  {
    slug: "new-mexico",
    name: "New Mexico",
    abbr: "NM",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star ticks are rare or not established in New Mexico based on public surveillance.",
    awareness:
      "New Mexico lies near the western limit, with limited or under-documented presence. General tick prevention still applies.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
    nearbyStateSlugs: ["arizona", "texas", "oklahoma", "colorado"],
  },
  {
    slug: "new-york",
    name: "New York",
    abbr: "NY",
    risk: "established",
    peakSeason: "April – September",
    seasonSource: { sourceId: "rutgers-fs1281", scope: "regional" },
    summary:
      "Lone Star ticks are established on Long Island, where CDC counted more suspected alpha-gal cases than in any other county it named. CDC lists 19 New York counties as established and 32 more where the tick has been reported.",
    awareness:
      "Suffolk County had 3,746 suspected alpha-gal cases in 2017–2022, about 4% of the U.S. total. Use tick prevention across the state, upstate included.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "rutgers-fs1281"],
    nearbyStateSlugs: ["new-jersey", "pennsylvania", "connecticut", "massachusetts", "vermont"],
    countyData: [
      { name: "Suffolk County (Long Island)", awarenessLevel: "high", source: MMWR_NAMED.suffolk },
      {
        name: "Nassau County (Long Island)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Westchester County", awarenessLevel: "established", source: CDC_ESTABLISHED },
    ],
  },
  {
    slug: "north-carolina",
    name: "North Carolina",
    abbr: "NC",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "ncdhhs-2022", scope: "state" },
    summary:
      "CDC lists Lone Star ticks as established in 48 of North Carolina's 100 counties, and found some of the highest rates of suspected alpha-gal cases in the state.",
    awareness:
      "Hikers, hunters and yard workers should check for ticks after time outdoors, from spring into early fall.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "ncdhhs-2022"],
    nearbyStateSlugs: ["virginia", "south-carolina", "tennessee", "georgia"],
    countyData: [
      {
        name: "Wake County (Raleigh area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Mecklenburg County (Charlotte area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Guilford County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      {
        name: "New Hanover County (Wilmington area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Buncombe County (Asheville area)",
        awarenessLevel: "established",
        source: {
          sourceId: "mmwr-2026-biltmore",
          finding:
            "A 2024–2025 CDC study in Biltmore Forest found five Lone Star ticks. CDC's county record has none yet.",
        },
      },
    ],
  },
  {
    slug: "north-dakota",
    name: "North Dakota",
    abbr: "ND",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star ticks are rare or not established in North Dakota based on public surveillance.",
    awareness:
      "North Dakota lies outside the documented Lone Star tick range. Other tick species occur, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "ohio",
    name: "Ohio",
    abbr: "OH",
    risk: "established",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists Lone Star ticks as established in 19 Ohio counties and reported in 29 more. Ohio State researchers say they have grown in number and range in southern Ohio.",
    awareness:
      "Ohio is within the documented range, with greater presence in the south. Follow prevention in wooded and brushy areas during the active season.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "osu-klompen-2017"],
    nearbyStateSlugs: ["indiana", "kentucky", "west-virginia", "pennsylvania", "michigan"],
  },
  {
    slug: "oklahoma",
    name: "Oklahoma",
    abbr: "OK",
    risk: "high",
    peakSeason: "March – September",
    seasonSource: { sourceId: "mcclung-2023-ok", scope: "state" },
    summary:
      "Oklahoma is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Oklahoma. Hunters, outdoor workers and rural residents should take precautions.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "mcclung-2023-ok"],
    nearbyStateSlugs: ["texas", "arkansas", "missouri", "kansas"],
  },
  {
    slug: "oregon",
    name: "Oregon",
    abbr: "OR",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Oregon based on public surveillance.",
    awareness:
      "Oregon is west of the documented Lone Star tick range. Other tick species are present, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "pennsylvania",
    name: "Pennsylvania",
    abbr: "PA",
    risk: "established",
    peakSeason: "April – August",
    seasonSource: { sourceId: "pa-dep-lst", scope: "state" },
    summary:
      "Lone Star ticks can now be found across Pennsylvania, Penn State Extension says, though they are patchy: common in some places and missing in others.",
    awareness:
      "CDC lists them as established in eight Pennsylvania counties and reported in 11 more. Hikers and homeowners should follow prevention steps.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "pa-dep-lst", "psu-ext-lst"],
    nearbyStateSlugs: ["new-jersey", "new-york", "ohio", "maryland", "west-virginia", "delaware"],
  },
  {
    slug: "rhode-island",
    name: "Rhode Island",
    abbr: "RI",
    risk: "emerging",
    peakSeason: "April – August",
    seasonSource: { sourceId: "uri-tickencounter-lst", scope: "state" },
    summary:
      "CDC lists Lone Star ticks as established in two of Rhode Island's five counties, Bristol and Newport.",
    awareness:
      "Rhode Island is near the northern edge of the range. URI's TickEncounter says reports here have mostly come from Prudence Island. Follow prevention in brush, dunes and woods.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "uri-tickencounter-lst"],
    nearbyStateSlugs: ["connecticut", "massachusetts"],
  },
  {
    slug: "south-carolina",
    name: "South Carolina",
    abbr: "SC",
    risk: "established",
    peakSeason: "March – October",
    seasonSource: { sourceId: "ga-dph-tbd", scope: "regional" },
    summary: "CDC lists Lone Star ticks as established in 25 of South Carolina's 46 counties.",
    awareness:
      "CDC says South Carolina has fewer suspected alpha-gal cases than its neighbors, and nobody yet knows why. Residents and outdoor workers should still take tick steps through the long warm season.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "mmwr-2026-seroprevalence", "ga-dph-tbd"],
    nearbyStateSlugs: ["georgia", "north-carolina"],
    countyData: [
      {
        name: "Richland County (Columbia area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Charleston County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "Greenville County", awarenessLevel: "established", source: CDC_REPORTED },
      { name: "Lexington County", awarenessLevel: "established", source: CDC_REPORTED },
    ],
  },
  {
    slug: "south-dakota",
    name: "South Dakota",
    abbr: "SD",
    risk: "emerging",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists Lone Star ticks as established in four southeastern South Dakota counties: Clay, McCook, Union and Yankton.",
    awareness:
      "Most of the state has no records yet. Use tick prevention outdoors, especially in the southeast.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data"],
  },
  {
    slug: "tennessee",
    name: "Tennessee",
    abbr: "TN",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "Tennessee is within the established Lone Star tick range and among the states where CDC found the highest rates of suspected alpha-gal cases.",
    awareness:
      "CDC found some of the highest rates of suspected alpha-gal cases in parts of Tennessee. Residents in rural and wooded areas should follow prevention through the warm months.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data"],
    nearbyStateSlugs: ["kentucky", "georgia", "north-carolina", "virginia", "missouri", "arkansas"],
    countyData: [
      {
        name: "Davidson County (Nashville area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Shelby County (Memphis area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Knox County (Knoxville area)", awarenessLevel: "established", source: CDC_REPORTED },
      {
        name: "Hamilton County (Chattanooga area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
    ],
  },
  {
    slug: "texas",
    name: "Texas",
    abbr: "TX",
    risk: "established",
    peakSeason: "March – August",
    seasonSource: { sourceId: "tamu-tickapp-lst", scope: "regional" },
    summary:
      "CDC lists Lone Star ticks as established in 118 of Texas's 254 counties and reported in 66 more.",
    awareness:
      "Residents in wooded and grassy areas should take preventive steps, especially in spring and summer.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "tamu-tickapp-lst"],
    nearbyStateSlugs: ["oklahoma", "arkansas", "louisiana", "new-mexico"],
    countyData: [
      { name: "Harris County (Houston area)", awarenessLevel: "established", source: CDC_REPORTED },
      {
        name: "Travis County (Austin area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      {
        name: "Bexar County (San Antonio area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Montgomery County", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "Bastrop County", awarenessLevel: "established", source: CDC_ESTABLISHED },
    ],
  },
  {
    slug: "utah",
    name: "Utah",
    abbr: "UT",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Utah based on public surveillance.",
    awareness:
      "Utah lies outside the documented Lone Star tick range. Other tick species occur, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "vermont",
    name: "Vermont",
    abbr: "VT",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star tick presence in Vermont is limited or under-documented based on public surveillance.",
    awareness:
      "Vermont is at the cooler northern edge of the region, where Lone Star records are sparse. Other ticks are common, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
    nearbyStateSlugs: ["new-hampshire", "massachusetts", "new-york"],
  },
  {
    slug: "virginia",
    name: "Virginia",
    abbr: "VA",
    risk: "high",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists Lone Star ticks as established in more than half of Virginia's counties and cities, and found some of the highest rates of suspected alpha-gal cases here.",
    awareness:
      "Two Virginia counties stand out in CDC's data: Bedford for its number of suspected alpha-gal cases and Charlotte for its rate. Take care during outdoor activities.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data"],
    nearbyStateSlugs: [
      "north-carolina",
      "west-virginia",
      "maryland",
      "tennessee",
      "kentucky",
      "district-of-columbia",
    ],
    countyData: [
      { name: "Bedford County", awarenessLevel: "high", source: MMWR_NAMED.bedford },
      { name: "Charlotte County", awarenessLevel: "high", source: MMWR_NAMED.charlotte },
      {
        name: "Henrico County (Richmond area)",
        awarenessLevel: "established",
        source: CDC_ESTABLISHED,
      },
      { name: "Virginia Beach (city)", awarenessLevel: "established", source: CDC_ESTABLISHED },
      { name: "Albemarle County", awarenessLevel: "established", source: CDC_ESTABLISHED },
    ],
  },
  {
    slug: "washington",
    name: "Washington",
    abbr: "WA",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary:
      "Lone Star ticks are rare or not established in Washington based on public surveillance.",
    awareness:
      "Washington is west of the documented Lone Star tick range. Other tick species are present, so follow general prevention outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
  {
    slug: "west-virginia",
    name: "West Virginia",
    abbr: "WV",
    risk: "established",
    peakSeason: "May – September",
    seasonSource: { sourceId: "wv-dhhr-2022", scope: "state" },
    summary:
      "CDC lists Lone Star ticks as established in 13 of West Virginia's 55 counties and reported in 14 more.",
    awareness:
      "West Virginia borders Kentucky, Virginia and Maryland, where CDC found some of the highest rates of suspected alpha-gal cases. Outdoor workers and rural residents should follow prevention.",
    sourceIds: [...SRC_AGS_REGION, "cdc-lst-county-data", "wv-dhhr-2022"],
    nearbyStateSlugs: ["virginia", "kentucky", "ohio", "maryland", "pennsylvania"],
  },
  {
    slug: "wisconsin",
    name: "Wisconsin",
    abbr: "WI",
    risk: "emerging",
    peakSeason: "April – September",
    seasonSource: { sourceId: "cdc-prevent-bites", scope: "national" },
    summary:
      "CDC lists one Wisconsin county (Dane) where Lone Star ticks are established and 26 where they have been reported.",
    awareness:
      "CDC also found moderate to high rates of suspected alpha-gal cases in some Wisconsin counties. Talk to a clinician about symptoms and follow general tick prevention.",
    sourceIds: [...SRC_EMERGING, "cdc-lst-county-data", "wi-ticks-ags"],
    nearbyStateSlugs: ["minnesota", "iowa", "illinois", "michigan"],
  },
  {
    slug: "wyoming",
    name: "Wyoming",
    abbr: "WY",
    risk: "low",
    peakSeason: "Not established here",
    seasonSource: { sourceId: "cdc-lst-county-data", scope: "state" },
    summary: "Lone Star ticks are rare or not established in Wyoming based on public surveillance.",
    awareness:
      "A Lone Star tick has been reported in Wyoming, but CDC lists no county where they are established. Other ticks live here, so follow general tick safety outdoors.",
    sourceIds: [...SRC_LIMITED, "cdc-lst-county-data"],
  },
];

export const STATES: StateInfo[] = SEED.map((s) => ({
  habitats: DEFAULT_HABITATS,
  preventionNotes: DEFAULT_PREVENTION,
  whoShouldBeAware: DEFAULT_AUDIENCE,
  sourceIds: DEFAULT_SOURCE_IDS,
  lastReviewed: "2026-06-02",
  ...s,
}));

export const ALL_US_STATES = [
  "Alabama",
  "Alaska",
  "Arizona",
  "Arkansas",
  "California",
  "Colorado",
  "Connecticut",
  "Delaware",
  "Florida",
  "Georgia",
  "Hawaii",
  "Idaho",
  "Illinois",
  "Indiana",
  "Iowa",
  "Kansas",
  "Kentucky",
  "Louisiana",
  "Maine",
  "Maryland",
  "Massachusetts",
  "Michigan",
  "Minnesota",
  "Mississippi",
  "Missouri",
  "Montana",
  "Nebraska",
  "Nevada",
  "New Hampshire",
  "New Jersey",
  "New Mexico",
  "New York",
  "North Carolina",
  "North Dakota",
  "Ohio",
  "Oklahoma",
  "Oregon",
  "Pennsylvania",
  "Rhode Island",
  "South Carolina",
  "South Dakota",
  "Tennessee",
  "Texas",
  "Utah",
  "Vermont",
  "Virginia",
  "Washington",
  "West Virginia",
  "Wisconsin",
  "Wyoming",
];

export const getState = (slug: string) => STATES.find((s) => s.slug === slug);

export const getStateByAbbr = (abbr: string) => STATES.find((s) => s.abbr === abbr.toUpperCase());

/**
 * County-page rollout gate. Wave 1 covers states in or near the established Lone Star
 * tick range (established / high / emerging), where per-county pages are genuinely
 * differentiated and search intent is highest. Low/limited states (mostly the Mountain
 * West and Pacific, where the tick isn't established) are held for a later wave — widen
 * this set to launch them. The route, sitemap, and state-page county grid all read this.
 */
export const COUNTY_WAVE_RISKS: ReadonlySet<RiskLevel> = new Set<RiskLevel>([
  "established",
  "high",
  "emerging",
]);

/** Whether per-county pages are live for a state (route + sitemap + state-page county grid). */
export function countyPagesEnabled(state: StateInfo): boolean {
  return COUNTY_WAVE_RISKS.has(state.risk);
}

/** Related nearby states: explicit list if provided, else same-risk peers. */
export function getNearbyStates(state: StateInfo): StateInfo[] {
  if (state.nearbyStateSlugs?.length) {
    return state.nearbyStateSlugs
      .map((slug) => getState(slug))
      .filter((s): s is StateInfo => Boolean(s));
  }
  return STATES.filter((s) => s.slug !== state.slug && s.risk === state.risk).slice(0, 4);
}

// Lives in ./states-index so light modules (redirects, the not-found page) can use it
// without pulling this file's prose into every page.
export { STATE_ABBR_TO_NAME } from "./states-index";
